<template>
  <div
    :class="[classes, typeClasses, columnsClasses]"
    :data-preload="[$options.name]"
  >
    <div role="heading" aria-level="2" class="headline" @click="expandHandler">
      <ArrowDropDownSvg
        :class="['headlineIcon', { rotateArrow: expandList }]"
        width="7"
        height="8"
      />

      <img
        v-if="hasImage"
        class="image"
        :alt="image.alt"
        :src="imageUrl(image.src, { w: 80 })"
        loading="lazy"
      />
      <span class="title qa-LinksList__title">
        {{ title }}
      </span>
    </div>

    <TransitionExpand
      :expand="expandList"
      :initial-expand-state="initialExpandState"
      :class="{ expanded: expandList }"
      max="768"
    >
      <div
        class="items itemsInner qa-LinksList__itemsInner"
        :class="{ modlesInner: hasImage }"
      >
        <div
          v-for="(item, index) in items"
          :key="index"
          class="link-wrapper"
          :class="{ 'arrow-icon': !item.icon }"
        >
          <Icon
            v-if="item.icon"
            :name="item.icon"
            class="flagIcon"
            width="21"
            height="15"
            :original="true"
            :inline="false"
            :lazy="true"
          />
          <ChevronRightSvg
            v-if="image.src"
            class="arrow-icon"
            :class="{ arrow: hasImage }"
            width="21"
            height="15"
            :lazy="true"
          />
          <!-- [ozlui] v-text directive is used for eliminating unnecessarily generated spaces. -->
          <a
            class="itemLink"
            :class="{ model: hasImage }"
            :href="item.url"
            :target="item.target || null"
            :rel="item.target === '_blank' ? 'noreferrer noopener' : null"
          >
            {{ item.title }}
            <span
              v-if="item.titleBold"
              class="itemLinkBold"
              v-text="item.titleBold"
            />
          </a>
        </div>
        <div class="itemSlot item">
          <slot />
        </div>
      </div>
    </TransitionExpand>
  </div>
</template>

<script>
import Icon from 'Components/00-generated/Icon';
import ArrowDropDownSvg from 'Components/00-generated/ArrowDropDownSvg';
import TransitionExpand from 'Components/01-atoms/transition-expand/TransitionExpand';
import globalMixin from 'Libs/mixins/globalMixin';
import imageMixin from 'Libs/mixins/imageMixin.js';
import ChevronRightSvg from 'Components/00-generated/ChevronRightSvg';

export default {
  name: 'LinksList',
  components: {
    TransitionExpand,
    Icon,
    ArrowDropDownSvg,
    ChevronRightSvg,
  },
  mixins: [globalMixin, imageMixin],
  props: {
    type: {
      type: String,
      default: '',
    },
    columns: {
      type: String,
      default: '',
    },
    title: {
      type: String,
      default: '',
    },
    items: {
      type: Array,
      default: () => [],
    },
    image: {
      type: Object,
      default: () => ({}),
    },
    initialExpandState: Boolean,
  },
  data: () => ({
    expandList: false,
  }),
  computed: {
    columnsClasses() {
      let classes;

      switch (this.$props.columns) {
        case 'five':
          classes = 'fiveColumns';
          break;
        case 'two':
          classes = 'twoColumns';
          break;

        default:
          classes = '';
          break;
      }

      return classes;
    },
    typeClasses() {
      let classes;

      switch (this.$props.type) {
        case 'channels':
          classes = 'channels';
          break;

        default:
          classes = '';
          break;
      }

      return classes;
    },
    hasImage() {
      return !!this.image.src;
    },
  },
  methods: {
    expandHandler() {
      const mediumMax = window.matchMedia('(max-width: 768px)').matches;
      if (mediumMax) {
        this.expandList = !this.expandList;
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import 'variables/breakpoints';
@import 'mixins';

.twoColumns {
  .itemsInner {
    column-count: 2;
    column-gap: 16px;
  }
}

.fiveColumns {
  .itemsInner {
    column-count: 5;
    column-gap: 16px;
  }
}

.headline {
  display: flex;
  align-items: center;
  margin: 8px 0;
  font-weight: bold;
  font-size: 18px;
  color: var(--color-mine-shaft);
  line-height: 30px;
  text-align: left;
}

.headlineIcon {
  margin-right: 8px;
  transform: rotate(0);
  transform-origin: center;
  transition: transform var(--time-S) ease-in-out;

  ::v-deep path {
    fill: var(--color-primary);
  }
}

.image {
  width: 55px;
  margin-right: 4px;
}

.title {
  line-height: 1;
  font-size: 15px;
}

.arrow {
  width: 16px;
}

.rotateArrow {
  transform: rotate(-180deg);
}

.itemsInner {
  text-align: left;
}

.items {
  overflow: hidden;
  transition: height var(--time-S) ease-in-out;
}

.link-wrapper {
  display: flex;
  align-items: center;
  line-height: 18px;
  padding: 4px;

  &.arrow-icon {
    align-items: flex-start;

    .arrow-icon {
      margin-top: 2px;
      min-width: 22px;
    }
  }
}

::v-deep .itemLink {
  color: var(--color-rolling-stone);
  text-decoration: none;
  align-items: center;
  cursor: pointer;
}

.itemLinkBold {
  font-weight: bold;
}

.itemSlot:empty {
  display: none;
  margin-top: 0;
}

.model {
  font-size: var(--font-size-SM);
  cursor: pointer;
  font-weight: 400;
  min-width: 150px;
}

.model .itemTitle {
  flex-grow: 1;
}

.channels {
  .itemsInner {
    display: flex;
    align-items: center;
    flex-wrap: wrap;

    @media #{$_mediaMDown} {
      flex-flow: column wrap;
      align-items: flex-start;
      max-height: 220px;
    }
  }

  .link-wrapper {
    margin: 0 20px 10px 0;
  }

  .item {
    padding-right: 20px;
    padding-bottom: 10px;
    margin-top: 0;
  }

  .flagIcon {
    height: 15px;
  }

  .expanded .flagIcon {
    transition-delay: 0.1ms;
    opacity: 1;
  }
}

.flagIcon {
  margin-right: var(--space-1);
}

@media #{$_mediaLDown} {
  .fiveColumns .itemsInner {
    column-count: 2;
  }
}

@media #{$_mediaMDown} {
  .linksList {
    padding: 0 8px;
    border-bottom: 1px solid var(--color-alto);
  }

  .twoColumns .itemsInner,
  .fiveColumns .itemsInner {
    column-count: 1;
  }

  .headline {
    cursor: pointer;
    font-size: var(--font-size-SM);
  }

  .itemsInner {
    padding: 0 16px 16px;
  }

  .modlesInner {
    padding: 0 0 16px;
  }

  .arrow {
    width: 18px;
    display: inherit;
    margin: 5px 0;
  }

  ::v-deep .itemLink {
    line-height: 30px;
    margin: 0;
  }

  .channels .flagIcon {
    height: 20px;
  }

  .title {
    font-size: var(--font-size-SM);
    padding-left: 0;
    line-height: unset;
  }

  .model {
    width: auto;
    margin-left: 0;
    border-bottom: 1px solid var(--color-alto);
    flex-direction: row-reverse;
  }

  .model:last-of-type {
    border-bottom: none;
  }
}

@media #{$_mediaLUp} {
  .headlineIcon {
    display: none;
  }

  .items {
    max-height: none;
  }
}
</style>
